import Cookies from 'js-cookie';
import { STORAGE_KEYS, DEFAULT_CONFIG } from '../types/wallet.js';

/**
 * Generate a unique session ID
 * @returns {string} Unique session identifier
 */
export const generateSessionId = () => {
  return `xgc_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

/**
 * Generate a secure auth token
 * @returns {string} Secure authentication token
 */
export const generateAuthToken = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  const hash = btoa(`${timestamp}_${random}`);
  return hash;
};

/**
 * Set wallet data in all storage mechanisms
 * @param {string} walletAddress - The wallet address
 * @param {Object} sessionData - Additional session data
 */
export const setWalletSession = (walletAddress, sessionData = {}) => {
  const sessionId = generateSessionId();
  const authToken = generateAuthToken();
  const expiresAt = Date.now() + (sessionData.duration || DEFAULT_CONFIG.SESSION_DURATION);

  const sessionInfo = {
    sessionId,
    authToken,
    walletAddress,
    expiresAt,
    createdAt: Date.now(),
    ...sessionData
  };

  try {
    // Store in localStorage
    localStorage.setItem(STORAGE_KEYS.WALLET_ADDRESS, walletAddress);
    localStorage.setItem(STORAGE_KEYS.WALLET_CONNECTED, 'true');
    localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, authToken);

    // Store in sessionStorage
    sessionStorage.setItem(STORAGE_KEYS.WALLET_ADDRESS, walletAddress);
    sessionStorage.setItem(STORAGE_KEYS.WALLET_CONNECTED, 'true');
    sessionStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);

    // Store in cookies with secure settings
    const cookieOptions = {
      expires: 7, // 7 days
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'strict'
    };

    Cookies.set(STORAGE_KEYS.WALLET_ADDRESS, walletAddress, cookieOptions);
    Cookies.set(STORAGE_KEYS.WALLET_CONNECTED, 'true', cookieOptions);
    Cookies.set(STORAGE_KEYS.SESSION_ID, sessionId, cookieOptions);

    console.log('Wallet session created:', { sessionId, walletAddress });
    return sessionInfo;

  } catch (error) {
    console.error('Failed to set wallet session:', error);
    throw new Error('Session creation failed');
  }
};

/**
 * Get wallet session from storage
 * @returns {Object|null} Session information or null if not found
 */
export const getWalletSession = () => {
  try {
    // Try localStorage first
    let walletAddress = localStorage.getItem(STORAGE_KEYS.WALLET_ADDRESS);
    let isConnected = localStorage.getItem(STORAGE_KEYS.WALLET_CONNECTED) === 'true';
    let sessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
    let authToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

    // Fallback to sessionStorage
    if (!walletAddress) {
      walletAddress = sessionStorage.getItem(STORAGE_KEYS.WALLET_ADDRESS);
      isConnected = sessionStorage.getItem(STORAGE_KEYS.WALLET_CONNECTED) === 'true';
      sessionId = sessionStorage.getItem(STORAGE_KEYS.SESSION_ID);
    }

    // Fallback to cookies
    if (!walletAddress) {
      walletAddress = Cookies.get(STORAGE_KEYS.WALLET_ADDRESS);
      isConnected = Cookies.get(STORAGE_KEYS.WALLET_CONNECTED) === 'true';
      sessionId = Cookies.get(STORAGE_KEYS.SESSION_ID);
    }

    if (!walletAddress || !isConnected) {
      return null;
    }

    return {
      walletAddress,
      isConnected,
      sessionId,
      authToken,
      retrievedFrom: walletAddress === localStorage.getItem(STORAGE_KEYS.WALLET_ADDRESS) ? 'localStorage' : 
                     walletAddress === sessionStorage.getItem(STORAGE_KEYS.WALLET_ADDRESS) ? 'sessionStorage' : 'cookies'
    };

  } catch (error) {
    console.error('Failed to get wallet session:', error);
    return null;
  }
};

/**
 * Clear wallet session from all storage mechanisms
 */
export const clearWalletSession = () => {
  try {
    // Clear localStorage
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });

    // Clear sessionStorage
    Object.values(STORAGE_KEYS).forEach(key => {
      sessionStorage.removeItem(key);
    });

    // Clear cookies
    Object.values(STORAGE_KEYS).forEach(key => {
      Cookies.remove(key);
    });

    console.log('Wallet session cleared from all storage');

  } catch (error) {
    console.error('Failed to clear wallet session:', error);
  }
};

/**
 * Check if session is valid and not expired
 * @param {Object} session - Session object
 * @returns {boolean} Whether session is valid
 */
export const isSessionValid = (session) => {
  if (!session || !session.walletAddress) {
    return false;
  }

  // Check if session has expiration and if it's expired
  if (session.expiresAt && Date.now() > session.expiresAt) {
    console.log('Session expired');
    return false;
  }

  return true;
};

/**
 * Refresh session expiration
 * @param {string} walletAddress - The wallet address
 * @param {number} duration - Session duration in milliseconds
 */
export const refreshSession = (walletAddress, duration = DEFAULT_CONFIG.SESSION_DURATION) => {
  const session = getWalletSession();
  if (!session || session.walletAddress !== walletAddress) {
    return false;
  }

  try {
    const newExpiresAt = Date.now() + duration;
    
    // Update localStorage
    localStorage.setItem(STORAGE_KEYS.LAST_BALANCE_CHECK, Date.now().toString());
    
    // Update cookies
    const cookieOptions = {
      expires: 7,
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'strict'
    };
    
    Cookies.set(STORAGE_KEYS.WALLET_ADDRESS, walletAddress, cookieOptions);
    Cookies.set(STORAGE_KEYS.WALLET_CONNECTED, 'true', cookieOptions);

    console.log('Session refreshed for wallet:', walletAddress);
    return true;

  } catch (error) {
    console.error('Failed to refresh session:', error);
    return false;
  }
};

/**
 * Store XUMM payload ID for tracking
 * @param {string} payloadId - XUMM payload ID
 */
export const storePayloadId = (payloadId) => {
  try {
    localStorage.setItem(STORAGE_KEYS.XUMM_PAYLOAD_ID, payloadId);
    sessionStorage.setItem(STORAGE_KEYS.XUMM_PAYLOAD_ID, payloadId);
    Cookies.set(STORAGE_KEYS.XUMM_PAYLOAD_ID, payloadId, { expires: 1 }); // 1 day
  } catch (error) {
    console.error('Failed to store payload ID:', error);
  }
};

/**
 * Get stored XUMM payload ID
 * @returns {string|null} Payload ID or null
 */
export const getStoredPayloadId = () => {
  try {
    return localStorage.getItem(STORAGE_KEYS.XUMM_PAYLOAD_ID) ||
           sessionStorage.getItem(STORAGE_KEYS.XUMM_PAYLOAD_ID) ||
           Cookies.get(STORAGE_KEYS.XUMM_PAYLOAD_ID) ||
           null;
  } catch (error) {
    console.error('Failed to get stored payload ID:', error);
    return null;
  }
};

/**
 * Clear stored XUMM payload ID
 */
export const clearPayloadId = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.XUMM_PAYLOAD_ID);
    sessionStorage.removeItem(STORAGE_KEYS.XUMM_PAYLOAD_ID);
    Cookies.remove(STORAGE_KEYS.XUMM_PAYLOAD_ID);
  } catch (error) {
    console.error('Failed to clear payload ID:', error);
  }
};

/**
 * Set last balance check timestamp
 * @param {number} timestamp - Timestamp of last balance check
 */
export const setLastBalanceCheck = (timestamp = Date.now()) => {
  try {
    const timestampStr = timestamp.toString();
    localStorage.setItem(STORAGE_KEYS.LAST_BALANCE_CHECK, timestampStr);
    sessionStorage.setItem(STORAGE_KEYS.LAST_BALANCE_CHECK, timestampStr);
  } catch (error) {
    console.error('Failed to set last balance check:', error);
  }
};

/**
 * Get last balance check timestamp
 * @returns {number|null} Timestamp or null
 */
export const getLastBalanceCheck = () => {
  try {
    const timestamp = localStorage.getItem(STORAGE_KEYS.LAST_BALANCE_CHECK) ||
                     sessionStorage.getItem(STORAGE_KEYS.LAST_BALANCE_CHECK);
    return timestamp ? parseInt(timestamp, 10) : null;
  } catch (error) {
    console.error('Failed to get last balance check:', error);
    return null;
  }
};

/**
 * Check if balance check is needed
 * @param {number} interval - Check interval in milliseconds
 * @returns {boolean} Whether balance check is needed
 */
export const isBalanceCheckNeeded = (interval = DEFAULT_CONFIG.BALANCE_CHECK_INTERVAL) => {
  const lastCheck = getLastBalanceCheck();
  if (!lastCheck) return true;
  
  return (Date.now() - lastCheck) > interval;
}; 